import React, { useCallback, useMemo } from 'react'

import ListItem from 'components/Luxkit/List/ListItem'
import LandmarkIcon from 'components/Luxkit/Icons/custom/LandmarkIcon'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import LineMapMarkerIcon from 'components/Luxkit/Icons/line/LineMapMarkerIcon'
import switchFunc from 'lib/switchFunc'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'

export const SEARCH_MENU_LIST_ITEM_CLASS_NAME = 'search-menu-list-item'

interface Props {
  searchItem: App.SearchItem
  onClick?: (searchItem: App.SearchItem, mouseEvent?: React.MouseEvent<HTMLElement>) => void
  ['data-testid']?: string
  searchText?: string
}

const searchItemPropertiesSwitch = switchFunc({
  city: { Icon: LineMapMarkerIcon, typeIndicator: 'City' },
  state: { Icon: LineMapMarkerIcon, typeIndicator: 'State' },
  province: { Icon: LineMapMarkerIcon, typeIndicator: 'Province' },
  landmark: { Icon: LandmarkIcon, typeIndicator: 'Landmark' },
  property: { Icon: LineBedIcon, typeIndicator: 'Hotel' },
  destination: { Icon: LineMapMarkerIcon, typeIndicator: null },
  airport: { Icon: LinePlaneFlyIcon, typeIndicator: null },
}, { Icon: LineMapMarkerIcon, typeIndicator: null })

function LocationSearchFieldListOption(props: Props) {
  const {
    searchItem,
    onClick,
    searchText,
  } = props

  function highlightSearchMatch(search: string, text: string) {
    if (!search) return text

    const textMatchIndex = text.toLowerCase().indexOf(search.toLocaleLowerCase())

    if (textMatchIndex >= 0) {
      return (
        <>
          {text.substring(0, textMatchIndex)}
          <b>{text.substring(textMatchIndex, textMatchIndex + search.length)}</b>
          {text.substring(textMatchIndex + search.length, text.length)}
        </>
      )
    }

    return text
  }

  const {
    Icon,
    subtitle,
    title,
  } = useMemo(() => {
    const properties = searchItemPropertiesSwitch(searchItem.searchType === 'destination' ? searchItem.destinationType : searchItem.searchType)

    const { mainText, secondaryText } = searchItem.format
    const { Icon, typeIndicator } = properties

    const subtitleItems: string[] = []

    if (typeIndicator) {
      subtitleItems.push(typeIndicator)
    }

    if (
      secondaryText &&
      mainText.localeCompare(secondaryText, 'en', { sensitivity: 'base' }) !== 0
    ) {
      subtitleItems.push(secondaryText)
    }

    return {
      Icon,
      subtitle: highlightSearchMatch(searchText, subtitleItems.join(' · ')),
      title: highlightSearchMatch(searchText, mainText),
    }
  }, [searchItem, searchText])

  const handleClick = useCallback((mouseEvent: React.MouseEvent<HTMLElement>) => {
    onClick?.(searchItem, mouseEvent)
  }, [searchItem, onClick])

  return <ListItem
    className={SEARCH_MENU_LIST_ITEM_CLASS_NAME}
    startIcon={<Icon />}
    label={title}
    subtitle={subtitle}
    onClick={handleClick}
    data-testid={props['data-testid']}
  />
}

export default LocationSearchFieldListOption
